import * as _ from 'lodash';
import { useMemo } from 'react';

import { finleyAssert } from '@finley-technologies/common';

import { TAccountTypeGQLType } from '../../../../../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { getUnusedFeeFormValues } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/expense-mechanics/[expenseId]/dataMappings/getFormValues';
import { useCreditAgreementStore } from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';
import { computeDiff } from '~/core/routes/settings/implementations/common/DiffInputs/hooks';

export const useGetLiabilityAccountsByTAccount = (
  tAccount: Extract<TAccountTypeGQLType, 'unusedInterestExpense'>,
) => {
  return useCreditAgreementStore((store) =>
    store.creditAgreement?.liabilityAccounts?.filter(
      (la) => la.tAccount === tAccount,
    ),
  );
};

export const useGetCapitalProviderOptionsByTAccount = (
  counterpartySlug: string,
  tAccount: Extract<TAccountTypeGQLType, 'unusedInterestExpense'>,
) => {
  const unusedFeeLiabilityAccounts = useGetLiabilityAccountsByTAccount(
    tAccount,
  );

  const doesCounterpartySlugFeeExist = useMemo(() => {
    return unusedFeeLiabilityAccounts?.some(
      (la) =>
        la.counterpartySlug === counterpartySlug && la.tAccount === tAccount,
    );
  }, [unusedFeeLiabilityAccounts, counterpartySlug, tAccount]);

  const counterpartyName = useCreditAgreementStore(
    (store) =>
      store.creditAgreement?.facilityMechanics?.parties?.capitalProviders?.find(
        (cp) => cp.entity.slug === counterpartySlug,
      )?.entity.name,
  );

  finleyAssert(counterpartyName, 'Counterparty name was not found in Parties');

  const capitalProviderOptions = useCreditAgreementStore((store) => {
    return doesCounterpartySlugFeeExist
      ? store.creditAgreement?.facilityMechanics?.parties?.capitalProviders?.map(
          (cp) => ({
            label: cp.entity.name,
            value: cp.entity.slug,
          }),
        ) ?? []
      : [{ label: counterpartyName, value: counterpartySlug }];
  });

  return capitalProviderOptions;
};

export const useExpenseDiffResult = (
  counterpartySlug: string,
  tAccount: string,
) => {
  return useCreditAgreementStore((store) => {
    const previousCreditAgreement = store.previousCreditAgreement;
    const currentCreditAgreement = store.creditAgreement;

    if (previousCreditAgreement && currentCreditAgreement) {
      const previousLiabilityAccounts = previousCreditAgreement.liabilityAccounts?.filter(
        (la) => la.tAccount === tAccount,
      );
      const currentLiabilityAccounts = currentCreditAgreement.liabilityAccounts?.filter(
        (la) => la.tAccount === tAccount,
      );

      const previousExpenseFormData = getUnusedFeeFormValues(
        counterpartySlug,
        previousLiabilityAccounts,
      );

      const currentExpenseFormData = getUnusedFeeFormValues(
        counterpartySlug,
        currentLiabilityAccounts,
      );

      const diffResult = computeDiff(
        previousExpenseFormData,
        currentExpenseFormData,
        _.isEqual,
      );

      return { diffResult, previousExpenseFormData, currentExpenseFormData };
    }
    return undefined;
  }, _.isEqual);
};
